import { createRef, useEffect, useState } from 'react';

import clsxm from '@/lib/clsxm';

import { IconColor } from '@/components/icons/base-icon';
import ChevronLeftIcon from '@/components/icons/chevron-left-icon';
import ChevronRightIcon from '@/components/icons/chevron-right-icon';

import { CarouselItem, CarouselVariant } from '@/features/carousel';

export type ScrollableCarouselProps = {
  variant: CarouselVariant.SCROLLABLE;
  items: CarouselItem[];
};

export default function ScrollableCarousel({ items }: ScrollableCarouselProps) {
  const containerRef = createRef<HTMLDivElement>();
  const [percentageScrolled, setPercentageScrolled] = useState<number>(0.0);
  const [
    containerIsWiderThanScrollableArea,
    setContainerIsWiderThanScrollableArea,
  ] = useState<boolean>(false);

  const showRightArrow =
    percentageScrolled < 0.8 && !containerIsWiderThanScrollableArea;
  const showLeftArrow =
    percentageScrolled > 0.2 && !containerIsWiderThanScrollableArea;

  const onArrowClick = (direction: ArrowDirection) => {
    const container = containerRef.current;
    if (!container) return;

    // current left position + width of container
    const left =
      direction === ArrowDirection.LEFT
        ? container.scrollLeft - container.clientWidth
        : container.scrollLeft + container.clientWidth;

    container.scrollTo({
      left,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const percentageScrolled = scrollLeft / (scrollWidth - clientWidth);
      setPercentageScrolled(percentageScrolled);
      setContainerIsWiderThanScrollableArea(clientWidth >= scrollWidth);
    };

    onScroll();
    container?.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onScroll);

    return () => {
      container?.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [containerRef]);

  return (
    <div className='relative w-full'>
      {showLeftArrow && (
        <ArrowButton direction={ArrowDirection.LEFT} onClick={onArrowClick} />
      )}

      <div
        className='select-scrollbar flex w-full flex-nowrap items-stretch gap-8 overflow-x-auto py-6'
        ref={containerRef}
      >
        {items.map(({ id, component }) => (
          <div key={id} className='flex items-center justify-center'>
            {component}
          </div>
        ))}
      </div>

      {showRightArrow && (
        <ArrowButton direction={ArrowDirection.RIGHT} onClick={onArrowClick} />
      )}
    </div>
  );
}

enum ArrowDirection {
  LEFT,
  RIGHT,
}

type ArrowButtonProps = {
  direction: ArrowDirection;
  onClick?: (direction: ArrowDirection) => void;
};

const ArrowButton = ({ direction, onClick }: ArrowButtonProps) => {
  const Icon =
    direction === ArrowDirection.LEFT ? ChevronLeftIcon : ChevronRightIcon;
  return (
    <button
      onClick={() => onClick && onClick(direction)}
      className={clsxm(
        'absolute top-1/2 z-10 transform rounded-full bg-typography-extra-light p-2 shadow-xl',
        'hidden md:flex',
        {
          'right-0 -translate-y-1/2': direction === ArrowDirection.RIGHT,
          'left-0 -translate-y-1/2': direction === ArrowDirection.LEFT,
        }
      )}
    >
      <Icon color={IconColor.TEXT} />
    </button>
  );
};
